import React, { useState, useRef } from 'react';
import { 
  Paper, 
  Box, 
  Typography, 
  CircularProgress,
  Alert,
  useTheme
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';

const FileUploader = ({ onFileUpload }) => {
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const theme = useTheme();

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const handleButtonClick = () => {
    inputRef.current.click();
  };

  const processFile = (file) => {
    setError('');
    
    // Check file extension
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.edi') && !fileName.endsWith('.000') && !fileName.endsWith('.004')) {
      setError('Please upload a valid .edi, .000, or .004 file');
      return;
    }

    setLoading(true);
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const content = event.target.result;
        onFileUpload(content, file);
      } catch (err) {
        setError('Failed to process the file. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    reader.onerror = () => {
      setError('Failed to read the file. Please try again.');
      setLoading(false);
    };
    
    reader.readAsText(file);
  };

  return (
    <Paper
      elevation={0}
      className={`file-upload-container ${dragActive ? 'file-upload-active' : ''}`}
      sx={{
        p: { xs: 4, md: 6 },
        my: 2,
        textAlign: 'center',
        backgroundColor: theme.palette.mode === 'dark' 
          ? 'rgba(59, 130, 246, 0.05)' 
          : 'rgba(59, 130, 246, 0.02)',
        border: dragActive 
          ? `3px dashed ${theme.palette.primary.main}` 
          : `3px dashed ${theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
        borderRadius: 4,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        backdropFilter: 'blur(10px)',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          backgroundColor: theme.palette.mode === 'dark' 
            ? 'rgba(59, 130, 246, 0.1)' 
            : 'rgba(59, 130, 246, 0.05)',
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 20px 40px rgba(59, 130, 246, 0.2)' 
            : '0 20px 40px rgba(59, 130, 246, 0.1)',
          transform: 'translateY(-4px) scale(1.02)'
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #3b82f6, #06b6d4, #10b981)',
          opacity: dragActive ? 1 : 0,
          transition: 'opacity 0.3s ease',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: theme.palette.mode === 'dark' 
            ? 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(59, 130, 246, 0.05) 0%, transparent 70%)',
          transform: 'translate(-50%, -50%)',
          opacity: dragActive ? 1 : 0,
          transition: 'all 0.4s ease',
          pointerEvents: 'none'
        }
      }}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={handleButtonClick}
      role="button"
      tabIndex={0}
      aria-label="Upload EDI file"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleButtonClick();
        }
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".edi,.000,.004"
        onChange={handleChange}
        style={{ display: 'none' }}
        aria-label="Upload EANCOM file"
      />
      
      <Box sx={{ py: 3, position: 'relative' }}>
        <Box
          sx={{
            width: { xs: 100, md: 120 },
            height: { xs: 100, md: 120 },
            borderRadius: '50%',
            background: dragActive 
              ? 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)'
              : theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(6, 182, 212, 0.15) 100%)'
                : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(6, 182, 212, 0.1) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 4,
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: dragActive 
              ? '0 20px 40px rgba(59, 130, 246, 0.3)' 
              : theme.palette.mode === 'dark'
                ? '0 10px 20px rgba(0, 0, 0, 0.3)'
                : '0 10px 20px rgba(59, 130, 246, 0.1)',
            position: 'relative',
            overflow: 'hidden',
            transform: dragActive ? 'scale(1.1) rotate(5deg)' : 'scale(1)',
            border: dragActive ? '2px solid rgba(255, 255, 255, 0.3)' : 'none',
          }}
        >
          {/* Decorative circles */}
          <Box
            sx={{
              position: 'absolute',
              top: '15%',
              left: '15%',
              width: 15,
              height: 15,
              borderRadius: '50%',
              background: theme.palette.mode === 'dark' ? 'rgba(38, 198, 218, 0.3)' : 'rgba(0, 176, 204, 0.2)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '20%',
              right: '15%',
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: theme.palette.mode === 'dark' ? 'rgba(92, 107, 192, 0.3)' : 'rgba(63, 81, 181, 0.2)',
            }}
          />
          
          <Box sx={{ 
            position: 'relative', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            animation: dragActive ? 'pulse 1.5s infinite' : 'none',
            '@keyframes pulse': {
              '0%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.08)' },
              '100%': { transform: 'scale(1)' },
            }
          }}>
            <FileUploadIcon 
              sx={{ 
                fontSize: 54, 
                color: dragActive ? theme.palette.primary.main : theme.palette.primary.light, 
                transition: 'all 0.3s ease',
                filter: 'drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2))',
                transform: dragActive ? 'translateY(-4px)' : 'none',
                zIndex: 2,
              }} 
            />
            {dragActive && (
              <Box
                sx={{
                  position: 'absolute',
                  width: '140%',
                  height: '140%',
                  borderRadius: '50%',
                  background: theme.palette.mode === 'dark' 
                    ? 'radial-gradient(circle, rgba(38, 198, 218, 0.15) 0%, rgba(92, 107, 192, 0) 70%)'
                    : 'radial-gradient(circle, rgba(0, 176, 204, 0.1) 0%, rgba(63, 81, 181, 0) 70%)',
                  animation: 'ripple 1.5s infinite',
                  '@keyframes ripple': {
                    '0%': { transform: 'scale(0.7)', opacity: 1 },
                    '100%': { transform: 'scale(1.3)', opacity: 0 },
                  }
                }}
              />
            )}
          </Box>
        </Box>
        
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            fontWeight: 800, 
            color: dragActive ? 'white' : 'text.primary',
            mb: 2,
            transition: 'all 0.4s ease',
            textShadow: dragActive ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none',
            fontSize: { xs: '1.5rem', md: '2rem' }
          }}
        >
          {dragActive ? '✨ Release to upload' : '📄 Drop your EDI file here'}
        </Typography>
        
        <Typography 
          variant="h6" 
          color="text.secondary" 
          gutterBottom
          sx={{
            mb: 3,
            opacity: dragActive ? 1 : 0.8,
            transition: 'all 0.3s ease',
            fontWeight: 400,
            fontSize: { xs: '1rem', md: '1.1rem' }
          }}
        >
          or click to browse your computer
        </Typography>
        
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            mt: 1,
            px: 3,
            py: 1.2,
            borderRadius: 3,
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(92, 107, 192, 0.1)' 
              : 'rgba(63, 81, 181, 0.05)',
            border: theme.palette.mode === 'dark' 
              ? '1px solid rgba(92, 107, 192, 0.2)' 
              : '1px solid rgba(63, 81, 181, 0.1)',
            transition: 'all 0.3s ease',
            transform: dragActive ? 'scale(1.05)' : 'scale(1)',
          }}
        >
          <Typography 
            variant="body2" 
            sx={{
              color: 'text.secondary',
              fontWeight: 500
            }}
          >
            Accepted formats: .edi, .000, or .004
          </Typography>
        </Box>
        
        {loading && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            mt: 3,
            flexDirection: 'column',
          }}>
            <CircularProgress 
              color="primary" 
              sx={{ 
                color: 'secondary.main'
              }} 
            />
            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color: 'text.secondary',
                fontWeight: 500
              }}
            >
              Processing your file...
            </Typography>
          </Box>
        )}
        
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mt: 3, 
              mx: 'auto', 
              maxWidth: '90%',
              display: 'flex',
              alignItems: 'center',
              borderRadius: 2
            }}
          >
            {error}
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

export default FileUploader; 